import { KeyIcon } from '@phosphor-icons/react'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '@curri/components'

import { Panel, PANEL_HAIRLINE } from '../webhooks/Panel.tsx'
import { Pill } from '../webhooks/Pill.tsx'

import { Secret } from './Secret.tsx'

// The two keys, one row each, in the same rows-in-a-panel layout as the webhook
// endpoints card — same pills, same tones, so "Production" and "Sandbox" mean
// the same thing on both dashboard screens.

const rowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 14,
  padding: '14px 0',
  borderTop: `1px solid ${PANEL_HAIRLINE}`,
} as const

const KeyRow = ({
  label,
  tone,
  value,
  missing,
}: {
  label: string
  tone: 'info' | 'neutral'
  value: string | null
  /** What to say when there is no key — "not issued" and "not visible" differ. */
  missing: string
}) => (
  <div style={rowStyle}>
    <Pill tone={tone}>{label}</Pill>
    {value === null ? (
      <span style={{ fontSize: 13, color: 'var(--curri-text-tertiary)' }}>
        {missing}
      </span>
    ) : (
      <Secret value={value} />
    )}
  </div>
)

const NoKeys = () => (
  <EmptyState size="small">
    <EmptyStateIcon>
      <KeyIcon />
    </EmptyStateIcon>
    <EmptyStateTitle>No API keys issued</EmptyStateTitle>
    <EmptyStateDescription>
      Your account does not have API keys yet. Email engineering@curri.com and
      we will issue them.
    </EmptyStateDescription>
  </EmptyState>
)

export const KeysCard = ({
  apiKey,
  sandboxKey,
}: {
  apiKey: string | null
  sandboxKey: string | null
}) => {
  const issued = [apiKey, sandboxKey].filter(
    (key): key is string => key !== null
  )

  return (
    <Panel style={{ paddingBottom: 10 }}>
      <Panel.Title
        meta={issued.length > 0 ? `${issued.length} issued` : undefined}
      >
        API keys
      </Panel.Title>
      <Panel.Description>
        The password half of your Basic credential. Production books real
        deliveries; sandbox exercises the same API without dispatching anyone.
        Treat both like passwords — they do not expire on their own.
      </Panel.Description>

      <div style={{ marginTop: 14 }}>
        {issued.length === 0 ? (
          <NoKeys />
        ) : (
          <>
            <KeyRow
              label="Production"
              missing="Not visible to this session"
              tone="info"
              value={apiKey}
            />
            <KeyRow
              label="Sandbox"
              missing="Not issued"
              tone="neutral"
              value={sandboxKey}
            />
          </>
        )}
      </div>
    </Panel>
  )
}
